import './tailwind.css';
import 'aos/dist/aos.css';
import 'swiper/css';
import 'swiper/css/pagination';
import Lenis from '@studio-freight/lenis';
import GLightbox from 'glightbox';
import Alpine from 'alpinejs'
import collapse from '@alpinejs/collapse'

Alpine.plugin(collapse)

const hubSpotConfig = window.ifactoryHubSpot || null;
let hubSpotFormsPromise;
let hubSpotTrackingPromise;
let hubSpotFirstInteractionReady = false;

function hasHubSpotModalFrames(root = document) {
    return Boolean(root.querySelector('.hs-form-frame'));
}

function loadScriptOnce({ id, src }) {
    if (!src) {
        return Promise.resolve();
    }

    const existing = document.getElementById(id);
    if (existing?.dataset.loaded === 'true') {
        return Promise.resolve(existing);
    }

    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => resolve(existing), { once: true });
            existing.addEventListener('error', reject, { once: true });
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', () => {
            script.dataset.loaded = 'true';
            resolve(script);
        }, { once: true });
        script.addEventListener('error', reject, { once: true });
        document.body.appendChild(script);
    });
}

function loadHubSpotForms() {
    if (!hubSpotConfig?.formsSrc || !hasHubSpotModalFrames()) {
        return Promise.resolve();
    }

    if (!hubSpotFormsPromise) {
        hubSpotFormsPromise = loadScriptOnce({
            id: 'ifactory-hubspot-forms-loader',
            src: hubSpotConfig.formsSrc,
        });
    }

    return hubSpotFormsPromise;
}

function loadHubSpotTracking() {
    if (!hubSpotConfig?.trackingSrc) {
        return Promise.resolve();
    }

    if (!hubSpotTrackingPromise) {
        hubSpotTrackingPromise = loadScriptOnce({
            id: 'hs-script-loader',
            src: hubSpotConfig.trackingSrc,
        });
    }

    return hubSpotTrackingPromise;
}

function triggerHubSpotOnInteraction() {
    if (hubSpotFirstInteractionReady) {
        return;
    }

    hubSpotFirstInteractionReady = true;
    void loadHubSpotTracking();
    void loadHubSpotForms();
}

function initHubSpotFirstInteraction() {
    if (!hubSpotConfig) {
        return;
    }

    const options = { passive: true };
    const onFirstInteraction = () => {
        interactionEvents.forEach((eventName) => {
            window.removeEventListener(eventName, onFirstInteraction, options);
        });
        triggerHubSpotOnInteraction();
    };
    const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

    interactionEvents.forEach((eventName) => {
        window.addEventListener(eventName, onFirstInteraction, options);
    });
}

function getHubSpotModalTarget(selector) {
    if (!selector || !selector.startsWith('#')) {
        return null;
    }

    const target = document.querySelector(selector);
    if (!target || !hasHubSpotModalFrames(target)) {
        return null;
    }

    return target;
}

async function ensureHubSpotModalReady(selector) {
    if (!getHubSpotModalTarget(selector)) {
        return;
    }

    await loadHubSpotForms();
}

// Case Studies Filter Component
Alpine.data('caseStudiesFilter', () => ({
    loading: false,

    async search() {
        const propertyType = document.getElementById('property-type')?.value || '';
        const location = document.getElementById('location')?.value || '';

        this.loading = true;

        try {
            const formData = new FormData();
            formData.append('action', 'filter_case_studies');
            formData.append('property_type', propertyType);
            formData.append('location', location);
            formData.append('nonce', window.caseStudiesAjax?.nonce || '');

            const response = await fetch(window.caseStudiesAjax?.ajaxUrl || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (data.success) {
                const grid = document.getElementById('case-studies-grid');
                if (grid) {
                    grid.innerHTML = data.data.html;
                }
            } else {
                console.error('Filter error:', data.data?.message);
            }
        } catch (error) {
            console.error('AJAX error:', error);
        } finally {
            this.loading = false;
        }
    }
}));

window.Alpine = Alpine
Alpine.start()

document.addEventListener('DOMContentLoaded', () => {
    console.log('Global modals ready');

    // Initialize the global lightbox instance
    const globalModal = GLightbox({
        selector: '[data-glightbox-global]',
        touchNavigation: false,
        closeOnOutsideClick: true,
        keyboardNavigation: true,
        loop: false,
    });

    // Utility: open a modal by URL hash
    async function openModalByHash() {
        const hash = window.location.hash;
        if (!hash) return;

        await ensureHubSpotModalReady(hash);

        // Reload GLightbox in case modals were injected dynamically
        globalModal.reload();

        const target = document.querySelector(hash);
        if (target) {
            globalModal.open({
                href: hash,
                type: 'inline',
            });
        }
    }

    document.querySelectorAll('[data-glightbox-global][href^="#"]').forEach((trigger) => {
        if (trigger.dataset.hubspotBound === 'true') {
            return;
        }

        trigger.dataset.hubspotBound = 'true';
        trigger.addEventListener('click', async (event) => {
            const href = trigger.getAttribute('href');
            if (!getHubSpotModalTarget(href)) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
            triggerHubSpotOnInteraction();
            await ensureHubSpotModalReady(href);
            globalModal.reload();
            globalModal.open({
                href,
                type: 'inline',
            });
        }, true);
    });

    // Try opening on page load
    setTimeout(() => {
        void openModalByHash();
    }, 300); // slight delay helps if inline modal markup has just been parsed

    // Listen for hash changes
    window.addEventListener('hashchange', () => {
        void openModalByHash();
    });
});

initHubSpotFirstInteraction();

function initLenis() {
    // Respect reduced motion
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const lenis = new Lenis({
        smoothWheel: !prefersReduced,   // optional
        smoothTouch: !prefersReduced,   // optional
        duration: 1.1,                  // tweak to taste
        // easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        // wheelMultiplier: 1,
        // touchMultiplier: 1.2,
        // orientation: 'vertical',
    });

    // RAF loop
    function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Optional: smooth scroll for in-page anchors with header offset
    const header = document.querySelector('[data-sticky-header]');
    const offset = header ? -header.offsetHeight : 0;

    document.querySelectorAll('a[href^="#"]').forEach((link) => {
        link.addEventListener('click', (e) => {
            const id = link.getAttribute('href');
            if (!id || id === '#') return;
            const target = document.querySelector(id);
            if (!target) return;

            e.preventDefault();
            lenis.scrollTo(target, { offset, lock: true });
        });
    });

    // Expose for debugging if you like
    window.lenis = lenis;
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLenis);
} else {
    initLenis();
}


// global JS
async function initAOS() {
    try {
        const { default: AOS } = await import('aos');
        AOS.init({ duration: 1000, once: false, disable: 'mobile' });
    } catch (e) { /* aos not installed — ignore */ }
}

// Utils
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const mq1199 = window.matchMedia('(max-width: 1199px)');
const mq787 = window.matchMedia('(max-width: 787px)');

function setMaxHeight(el, px) {
    if (!el) return;
    el.style.maxHeight = (px == null ? '' : px + 'px');
}

function closeAll(selectorItem, selectorContent) {
    $$(selectorItem).forEach(item => {
        item.classList.remove('active', 'open');
        const c = item.querySelector(selectorContent);
        if (c) setMaxHeight(c, 0);
    });
}

// Drop items (.drop-item -> .hidden-content)
function enableDropItems() {
    $$('.drop-item').forEach(item => {
        if (item.__bound) return;
        item.__bound = true;
        item.addEventListener('click', (e) => {
            if (e.target.closest('a')) return; // allow links inside
            e.preventDefault();
            const isActive = item.classList.contains('active');
            const content = item.querySelector('.hidden-content');
            closeAll('.drop-item', '.hidden-content');
            if (!isActive && content) {
                item.classList.add('active');
                setMaxHeight(content, content.scrollHeight);
            }
        });
    });
}
function resetDropItems() {
    closeAll('.drop-item', '.hidden-content');
    $$('.hidden-content').forEach(c => { c.style.maxHeight = ''; });
}

// Header dropdown boxes (.dropdown-box -> .option-list, uses --option-list-height)
function initHeaderDropdowns() {
    $$('.dropdown-box').forEach(box => {
        if (box.__bound) return;
        box.__bound = true;
        box.addEventListener('click', (e) => {
            e.stopPropagation();
            const list = box.querySelector('.option-list');
            const isOpen = box.classList.contains('open');
            if (isOpen) {
                box.classList.remove('open');
                box.style.setProperty('--option-list-height', '0px');
            } else if (list) {
                const h = list.scrollHeight;
                box.style.setProperty('--option-list-height', h + 'px');
                box.classList.add('open');
            }
        });
    });

    // Click outside to close header dropdowns and mobile menu
    document.addEventListener('click', (e) => {
        if (!e.target.closest('.dropdown-box, #mobile-menu-icon, .mobile-menu, .menu-item-has-children')) {
            $$('.dropdown-box').forEach(b => {
                b.classList.remove('open');
                b.style.setProperty('--option-list-height', '0px');
            });
            $('#mobile-menu-icon')?.classList.remove('open');
            $('.mobile-menu')?.classList.remove('open-menu');
            document.body.classList.remove('open-mobile-menu');
            $$('.sub-menu').forEach(ul => setMaxHeight(ul, 0));
            $$('.menu-item-has-children').forEach(li => li.classList.remove('open'));
        }
    });
}

// Mobile menu
function initMobileMenu() {
    const icon = $('#mobile-menu-icon');
    const menu = $('.mobile-menu');
    if (!icon || !menu) return;

    icon.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = icon.classList.toggle('open');
        menu.classList.toggle('open-menu', open);
        document.body.classList.toggle('open-mobile-menu', open);
    });

    // Nav items with children (click to toggle — ignore link clicks)
    $$('.menu-item-has-children').forEach(item => {
        if (item.__bound) return;
        item.__bound = true;
        item.addEventListener('click', (e) => {
            if (e.target.closest('a')) return;
            e.preventDefault();
            e.stopPropagation();
            const submenu = item.querySelector('.sub-menu');
            const willOpen = !item.classList.contains('open');

            // close others
            $$('.menu-item-has-children.open').forEach(openItem => {
                openItem.classList.remove('open');
                const sm = openItem.querySelector('.sub-menu');
                if (sm) setMaxHeight(sm, 0);
            });

            // toggle this
            item.classList.toggle('open', willOpen);
            if (submenu) setMaxHeight(submenu, willOpen ? submenu.scrollHeight : 0);
        });
    });
}

// Footer mobile dropdowns (.mobile-dropdown h5 -> .hidden-content)
function initFooterDropdowns() {
    $$('.mobile-dropdown h5').forEach(h => {
        if (h.__bound) return;
        h.__bound = true;
        h.addEventListener('click', () => {
            if (!mq787.matches) return;
            const parent = h.closest('.mobile-dropdown');
            const content = parent?.querySelector('.hidden-content');
            const willOpen = !content?.classList.contains('open');

            // close all
            $$('.mobile-dropdown .hidden-content.open').forEach(c => {
                c.classList.remove('open');
                setMaxHeight(c, 0);
            });
            $$('.mobile-dropdown h5.open').forEach(h5 => h5.classList.remove('open'));

            if (content) {
                content.classList.toggle('open', willOpen);
                setMaxHeight(content, willOpen ? content.scrollHeight : 0);
                h.classList.toggle('open', willOpen);
            }
        });
    });
}

// Active link highlighting
function markActiveLinks() {
    const here = window.location.origin + window.location.pathname.replace(/\/+$/, '/');
    $$('a[href]').forEach(a => {
        try {
            const url = new URL(a.href, window.location.origin);
            const norm = url.origin + url.pathname.replace(/\/+$/, '/');
            if (norm === here) a.classList.add('active-link');
        } catch { }
    });
}

// Search overlay (#search-overlay with .search-overlay-close)
function initSearchOverlay() {
    const trigger = document.querySelector('.header-right a[href="#"]');
    const overlay = document.getElementById('search-overlay');
    const closeBtn = document.querySelector('.search-overlay-close');
    if (!trigger || !overlay || !closeBtn) return;
    trigger.addEventListener('click', (e) => { e.preventDefault(); overlay.classList.add('open'); });
    closeBtn.addEventListener('click', () => overlay.classList.remove('open'));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') overlay.classList.remove('open'); });
}

// Apply drop-item behaviour only under 1200px (matches your original)
function applyDropItemsByViewport() {
    if (mq1199.matches) enableDropItems(); else resetDropItems();
}

document.addEventListener('DOMContentLoaded', () => {
    //initLenis();
    initAOS();

    initHeaderDropdowns();
    initMobileMenu();
    initFooterDropdowns();
    initSearchOverlay();
    markActiveLinks();

    applyDropItemsByViewport();
    mq1199.addEventListener('change', applyDropItemsByViewport);
});
